import { ROLES, type AppRole } from "@/constants/permissions";

/**
 * Org role tier model: org_owner > org_admin > subcontractor.
 *
 * Who may manage whom (mirrors the DB RPCs org_set_member_role /
 * org_remove_member and the has_permission tier rules — the DB is the source of
 * truth; these predicates only decide whether the UI shows a control so we
 * never offer an action the backend would reject):
 *
 *   target = org_owner     → nobody in-app, not even another owner (owners are
 *                            seeded at signup or assigned manually in the DB)
 *   target = org_admin      → only an org_owner
 *   target = subcontractor  → an org_owner OR an org_admin
 *
 * Platform admins (Franklin staff) bypass all of this at the DB layer; that is a
 * separate concern and intentionally not modeled here.
 */
export interface ActorTier {
  isOwner: boolean;
  isAdmin: boolean;
}

/**
 * Can the actor change the role of, or remove, a member who currently holds
 * `targetRole`? Self-actions are handled by the caller (it knows the actor's id).
 */
export function canManageMember(actor: ActorTier, targetRole: string): boolean {
  if (targetRole === ROLES.ORG_OWNER) return false;
  if (targetRole === ROLES.ORG_ADMIN) return actor.isOwner;
  return actor.isOwner || actor.isAdmin;
}

/**
 * Roles the actor may assign to a managed member, and equivalently invite as.
 * org_owner is never assignable from the app.
 */
export function assignableRolesFor(actor: ActorTier): AppRole[] {
  if (actor.isOwner) return [ROLES.ORG_ADMIN, ROLES.SUBCONTRACTOR];
  if (actor.isAdmin) return [ROLES.SUBCONTRACTOR];
  return [];
}

/**
 * Is the user a subcontractor in this org with no owner/admin role on top?
 * Such a member works inside their assigned projects only: the building shell
 * never offers them Building view (see useIsProjectScopedMember).
 */
export function isSubcontractorOnlyIn(
  hasRoleInOrg: (orgId: string, role: string) => boolean,
  orgId: string
): boolean {
  return (
    hasRoleInOrg(orgId, ROLES.SUBCONTRACTOR) &&
    !hasRoleInOrg(orgId, ROLES.ORG_OWNER) &&
    !hasRoleInOrg(orgId, ROLES.ORG_ADMIN)
  );
}
